import 'react-toggle/style.css'

import { useMutation } from '@tanstack/react-query'
import { useDebounce } from '@uidotdev/usehooks'
import { AxiosError } from 'axios'
import React, { useState } from 'react'
import Toggle from 'react-toggle'

import { COMPANY_PROFILE_TAB } from '@/components/misc/CompanyProfile/constants'
import { Spinner } from '@/components/ui/spinner'
import { useCompanyProfileSearchParams } from '@/hooks/useCompanyProfileSearchParams'
import { useInvalidateQueries } from '@/hooks/useInvalidateQueries'
import useNotyf from '@/hooks/useNotyf'
import { useShowGenericServerError } from '@/hooks/useShowGenericServerError'
import { QUERY_KEYS } from '@/lib/queryKeys'
import errorTypeService from '@/lib/services/errorTypeService'
import panelService from '@/lib/services/panelService'
import { cn } from '@/lib/utils'
import { useProfessionalNetworkFilters } from '@/pages/professionalNetworkRelationships/hooks/useProfessionalNetworkFilters'
import { CompanyProfileItemPanel } from '@/types/responses/companies'

interface Props {
  className?: string
  panel: CompanyProfileItemPanel
}

const ToggleInternalRelationship: React.FC<Props> = ({ panel, className }) => {
  const { searchParams, setTab } = useCompanyProfileSearchParams()
  const invalidateQueries = useInvalidateQueries()
  const [internalRelationship, setInternalRelationship] = useState(
    panel.internalRelationship,
  )
  const notyf = useNotyf()
  const [filters] = useProfessionalNetworkFilters()
  const debouncedCompanyName = useDebounce(filters.companyName, 300)
  const showGenericServerError = useShowGenericServerError()

  const toggleInternalRelationshipMutation = useMutation({
    mutationFn: (isInternalRelationship: boolean) =>
      panelService.updateInternalRelationship(panel.id, isInternalRelationship),
    onSuccess: async (updatedPanel) => {
      setInternalRelationship(updatedPanel.internalRelationship)

      await invalidateQueries([
        QUERY_KEYS.companyProfile({
          identifier: searchParams.identifier ?? '',
          identifierType: searchParams.identifierType ?? '',
        }),
        QUERY_KEYS.panelPartners({
          companyName: debouncedCompanyName,
        }),
        QUERY_KEYS.referrals(),
      ])
    },
    onError: (error: unknown) => {
      if (errorTypeService.isConstraintViolationListError(error)) {
        const errorMessage =
          error instanceof AxiosError
            ? (error?.response?.data?.violations[0]?.message ?? '')
            : ''

        if (errorMessage) {
          notyf.error(errorMessage)
          return
        }

        showGenericServerError()
      }

      setInternalRelationship(panel.internalRelationship)
    },
  })

  async function handleToggleChange(isInternalRelationship: boolean) {
    await toggleInternalRelationshipMutation.mutateAsync(isInternalRelationship)

    if (
      isInternalRelationship &&
      searchParams.profileTab === COMPANY_PROFILE_TAB.commercialAgreements
    ) {
      setTab(COMPANY_PROFILE_TAB.overview)
    }
  }

  return (
    <label
      className={cn(
        'flex items-center justify-end gap-2',
        { 'opacity-50': toggleInternalRelationshipMutation.isPending },
        className,
      )}
    >
      {toggleInternalRelationshipMutation.isPending && <Spinner />}

      <span id="toggle-internal-relationship">Internal Relationship</span>

      <Toggle
        aria-labelledby="toggle-internal-relationship"
        defaultChecked={internalRelationship}
        checked={internalRelationship}
        onChange={(event) => handleToggleChange(event.currentTarget.checked)}
      />
    </label>
  )
}

export default ToggleInternalRelationship
